import React from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'app';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Sparrow - 100일 개인성장 챌린지',
  description = '매일 90분으로 인생을 바꾸는 100일 개인성장 플랫폼. 신체활동과 정신활동으로 균형잡힌 성장을 경험하세요.',
  keywords = '개인성장, 100일 챌린지, 90분, 습관형성, 자기계발, 신체활동, 정신활동, 목표달성, 동기부여, 성장플랫폼, GOLDSAJU',
  image = 'https://sparrow100.pages.dev/icons/icon-512x512.svg',
  url = 'https://sparrow100.pages.dev',
  type = 'website',
  author = 'GOLDSAJU',
  publishedTime,
  modifiedTime
}) => {
  React.useEffect(() => {
    // 페이지 타이틀 설정
    document.title = title;

    // 메타 태그 업데이트 함수
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // 기본 메타 태그
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');

    // Open Graph 메타 태그
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Sparrow', true);
    updateMetaTag('og:locale', 'ko_KR', true);

    // Twitter Card 메타 태그
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:creator', '@SparrowApp');

    // Article 타입일 때 추가 메타 태그
    if (type === 'article' && publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true);
      updateMetaTag('article:author', author, true);
      
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, true);
      }
    }

    // App 타입일 때 추가 메타 태그
    if (type === 'app') {
      updateMetaTag('apple-mobile-web-app-capable', 'yes');
      updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
      updateMetaTag('apple-mobile-web-app-title', 'Sparrow');
      updateMetaTag('mobile-web-app-capable', 'yes');
      updateMetaTag('application-name', 'Sparrow');
      updateMetaTag('theme-color', '#D4AF37');
    }

    // Canonical URL 링크 태그
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // 구조화된 데이터 (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Sparrow",
      "description": description,
      "url": url,
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "KRW"
      },
      "author": {
        "@type": "Organization",
        "name": "GOLDSAJU",
        "email": "contact@goldsaju.com",
        "telephone": "+82 010-2719-4855",
        "url": url
      },
      "publisher": {
        "@type": "Organization",
        "name": "GOLDSAJU",
        "logo": {
          "@type": "ImageObject",
          "url": `${url}/icons/icon-512x512.svg`
        }
      },
      "image": {
        "@type": "ImageObject",
        "url": image,
        "width": 1200,
        "height": 630
      },
      "inLanguage": "ko-KR",
      "potentialAction": {
        "@type": "UseAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": url
        }
      }
    };

    // 기존 구조화된 데이터 제거
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // 새 구조화된 데이터 추가
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

  }, [title, description, keywords, image, url, type, author, publishedTime, modifiedTime]);

  return null; // 렌더링할 내용 없음
};

export default SEOHead; 